import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {Check} from "lucide-react";
import React from "react";
import {initials as initialsOf} from "common/helpers";
import {color, state, type} from "common/tokens";
import ShimmerImage from "components/redesign/ShimmerImage";

const SIZES = {
  card: 25,
  roster: 34,
  step: 30,
  answer: 30,
};

const useStyles = makeStyles({
  avatar: {
    position: 'relative',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: color.AVATAR_BG,
    color: color.AVATAR_TEXT,
    overflow: 'visible',
  },
  done: {
    background: color.AVATAR_DONE_BG,
    color: color.AVATAR_DONE_TEXT,
  },
  roster: {
    background: color.AVATAR_ROSTER_BG,
    color: color.AVATAR_DONE_TEXT,
  },
  // Session step rows: accent tint, with the readable on-tint text rather than
  // the accent itself, which does not carry on its own container.
  step: {
    background: color.ACCENT_TINT,
    color: color.ACCENT_TEXT,
  },
  // Quiz answers: a solid accent chip against the white answer card, so the
  // person who replied reads as clearly as the reply.
  answer: {
    background: color.ACCENT,
    color: color.ON_ACCENT,
  },
  ring: {
    border: `2px solid ${color.AVATAR_RING}`,
  },
  // Someone still to finish reads as faded. Applied to the whole chip rather
  // than the fill, because a profile photo covers the fill and would hide the
  // distinction entirely.
  pending: {
    opacity: state.DISABLED_OPACITY,
  },
  initials: {
    ...type.label,
  },
  initialsCard: {
    ...type.label,
    fontSize: 'var(--ui-text-avatar)',
  },
  image: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
  },
  badge: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: color.AVATAR_DONE_BG,
    color: color.AVATAR_DONE_TEXT,
    border: `1.5px solid ${color.SURFACE}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  group: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  overflow: {
    ...type.label,
    fontSize: 'var(--ui-text-avatar)',
    color: color.TEXT_MUTED,
    whiteSpace: 'nowrap',
  },
});

export const Avatar = ({
  name,
  label,
  image,
  isDone = false,
  variant = 'card',
  showBadge = false,
  ring = false,
  dimWhenPending = false,
}) => {
  const classes = useStyles();
  const size = SIZES[variant] || SIZES.card;
  const text = label || initialsOf(name);

  return (
    <span
      className={classNames(
        classes.avatar,
        variant === 'roster' && classes.roster,
        variant === 'step' && classes.step,
        variant === 'answer' && classes.answer,
        isDone && classes.done,
        dimWhenPending && !isDone && classes.pending,
        ring && classes.ring
      )}
      style={{width: size, height: size}}
      title={name || undefined}
    >
      {image
        ? <ShimmerImage src={image} className={classes.image}/>
        : <span className={variant === 'roster' ? classes.initials : classes.initialsCard}>{text}</span>}
      {isDone && showBadge && (
        <span className={classes.badge}><Check size={8} strokeWidth={4}/></span>
      )}
    </span>
  );
};

export const AvatarGroup = ({
  members = [],
  variant = 'card',
  ring = true,
  showBadge = true,
  dimWhenPending = false,
  max,
}) => {
  const classes = useStyles();
  const shown = max ? members.slice(0, max) : members;
  const hidden = members.length - shown.length;

  return (
    <span className={classes.group}>
      {shown.map((member, index) => (
        <Avatar
          key={`${member.label || member.name}-${index}`}
          {...member}
          variant={variant}
          ring={ring}
          showBadge={showBadge}
          dimWhenPending={dimWhenPending}
        />
      ))}
      {hidden > 0 && <span className={classes.overflow}>{`+${hidden}`}</span>}
    </span>
  );
};

export default Avatar;
